import {
  useRejectInstagramMutation,
  useRejectTiktokMutation,
  useVerifyInstagramMutation,
  useVerifyTiktokMutation,
  useVerifyUgcMutation,
} from "@/api/userApi";
import InfoIcon from "@/assets/colorable/info_outline.svg?react";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import TickIcon from "@/assets/colorable/tick.svg";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import UgcIcon from "@/assets/colorable/ugc.svg?react";
import HammerIcon from "@/assets/light_gradient/hammer.svg?react";
import Button from "@/components/Common/Button";
import Toggle from "@/components/Common/forms/Toggle";
import FullPageLoader from "@/components/Common/FullPageLoader";
import PermissionDenied from "@/components/Common/PermissionDenied";
import DisciplineHistory from "@/components/discipline/DisciplineHistory";
import DisciplineIndicators from "@/components/discipline/DisciplineIndicators";
import FormErrors from "@/components/Influencer/FormErrors";
import UGCVerification from "@/components/Influencer/UGCVerification";
import VerificationChannelRow from "@/components/Influencer/VerificationChannelRow";
import VerificationRejectPanel from "@/components/Influencer/VerificationRejectPanel";
import VerificationSourceNote from "@/components/Influencer/VerificationSourceNote";
import VerificationStatusPill from "@/components/Influencer/VerificationStatusPill";
import WalletActivity from "@/components/Influencer/WalletActivity";
import {PhoneInput} from "@/components/ui/PhoneInput";
import type {
  InfluencerProfileDetailSerializer,
  LatestVerificationSerializer,
  UGCVideoVerificationSerializer,
  UserBankAccountSerializer,
  UserSerializer,
} from "@/generated/DjangoTypes";
import {useDiscipline} from "@/hooks/useDiscipline";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useIsAdmin} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import ChangePasswordSection from "@/pages/profile/ChangePasswordSection";
import {Can} from "@/permissions";
import {isDisciplinaryModalOpenAtom} from "@/store/jotaiAtoms";
import useUserStore from "@/store/user";
import {isStreetNumberRequired} from "@/utils/addressUtils";
import {getResponseErrorMessages} from "@/utils/errorHandlers";
import {createFileRoute} from "@tanstack/react-router";
import dayjs from "dayjs";
import {useSetAtom} from "jotai";
import {useEffect, useState, type MouseEvent} from "react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";

const INSTAGRAM_REJECTION_REASONS = [
  {labelKey: "influencer_profile:rejection_reason_poor_content_quality", value: "7"},
  {labelKey: "influencer_profile:rejection_reason_product_tester", value: "6"},
  {labelKey: "influencer_profile:rejection_reason_demographics", value: "5"},
  {labelKey: "influencer_profile:rejection_reason_low_followers", value: "1"},
  {labelKey: "influencer_profile:rejection_reason_low_engagement_rate", value: "2"},
  {labelKey: "influencer_profile:rejection_reason_other", value: "3"},
] as const;

const REJECTION_SMS_REASON_TO_KEY = {
  activity: "influencer_profile:rejection_reason_activity",
  "bad content quality": "influencer_profile:rejection_reason_poor_content_quality",
  demographics: "influencer_profile:rejection_reason_demographics",
  "engagement rate": "influencer_profile:rejection_reason_low_engagement_rate",
  followers: "influencer_profile:rejection_reason_low_followers",
  other: "influencer_profile:rejection_reason_other",
  "product tester": "influencer_profile:rejection_reason_product_tester",
} as const;

const TIKTOK_REJECTION_REASONS = [
  {labelKey: "influencer_profile:rejection_reason_poor_content_quality", value: "7"},
  {labelKey: "influencer_profile:rejection_reason_product_tester", value: "6"},
  {labelKey: "influencer_profile:rejection_reason_demographics", value: "5"},
  {labelKey: "influencer_profile:rejection_reason_low_followers", value: "1"},
  {labelKey: "influencer_profile:rejection_reason_low_engagement_rate", value: "2"},
  {labelKey: "influencer_profile:rejection_reason_activity", value: "4"},
  {labelKey: "influencer_profile:rejection_reason_other", value: "3"},
] as const;

export const Route = createFileRoute("/_authenticated/influencer/$influencerId/edit")({
  component: InfluencerEdit,
  params: {
    parse: raw => ({influencerId: Number(raw.influencerId)}),
    stringify: params => ({influencerId: params.influencerId?.toString()}),
  },
});

function InfluencerEdit() {
  const {t} = useTranslation(["common", "influencer_discipline", "influencer_profile", "navigation"]);

  useTitle(t("navigation:edit_profile"));

  const {userId} = useFetchUser();
  const isAdmin = useIsAdmin();
  const {initializeDisciplineModal} = useDiscipline();
  const setDisciplinaryModalOpen = useSetAtom(isDisciplinaryModalOpenAtom);
  const [loading, setLoading] = useState(true);
  const [influencerDetails, setInfluencerDetails] = useState<InfluencerProfileDetailSerializer | null | undefined>(
    undefined,
  );
  // account state
  const [bankAccount, setBankAccount] = useState<UserBankAccountSerializer>();
  const [email, setEmail] = useState<string | undefined>("");
  const [firstName, setFirstName] = useState<string | undefined>("");
  const [lastName, setLastName] = useState<string | undefined>("");
  const [street, setStreet] = useState<string | null | undefined>(undefined);
  const [streetNumber, setStreetNumber] = useState<string | null | undefined>(undefined);
  const [postalCode, setPostalCode] = useState<string | null | undefined>(undefined);
  const [city, setCity] = useState<string | null | undefined>(undefined);
  const [country, setCountry] = useState<string | null | undefined>(undefined);
  const [phoneNumber, setPhoneNumber] = useState<string | null | undefined>(undefined);
  const [gender, setGender] = useState<string | null | undefined>(undefined);
  const [dateOfBirth, setDateOfBirth] = useState<string | null | undefined>(undefined);
  const [bio, setBio] = useState<string | null | undefined>("");
  const [isInstaVerified, setIsInstaVerified] = useState<boolean>(false);
  const [isTiktokVerified, setIsTiktokVerified] = useState<boolean>(false);
  const [tiktokRejectionSmsReason, setTiktokRejectionSmsReason] = useState<string | null | undefined>(undefined);
  const [rejectionSmsReason, setRejectionSmsReason] = useState<string | null | undefined>(undefined);
  const [latestVerification, setLatestVerification] = useState<LatestVerificationSerializer | null>(null);
  const [accountSuccess, setAccountSuccess] = useState<boolean | undefined>(false);
  const [isApplicationRestricted, setIsApplicationRestricted] = useState(false);
  const [isInstaPro, setIsInstaPro] = useState(false);
  const [isTikTokPro, setIsTikTokPro] = useState(false);

  // dress details
  const [top, setTop] = useState<string | null | undefined>("");
  const [below, setBelow] = useState<string | null | undefined>("");
  const [shoeSize, setShoeSize] = useState<string | null | undefined>("");
  const [personalStyle, setPersonalStyle] = useState<string | null | undefined>("");
  const [personalSuccess, setPersonalSuccess] = useState(false);

  // instagram state
  const [username, setUsername] = useState<string | null | undefined>(undefined);
  const [originalUsername, setOriginalUsername] = useState<string | null | undefined>(undefined);
  const [followers, setFollowers] = useState<number | null | undefined>(undefined);
  const [engagement, setEngagement] = useState<number | null | undefined>(undefined);
  const [instagramSuccess, setInstagramSuccess] = useState(false);

  // insights state
  const [cityOneName, setCityOneName] = useState<string | null | undefined>(undefined);
  const [cityOneShare, setCityOneShare] = useState<number | null | undefined>(undefined);
  const [cityTwoName, setCityTwoName] = useState<string | null | undefined>(undefined);
  const [cityTwoShare, setCityTwoShare] = useState<number | null | undefined>(undefined);
  const [cityThreeName, setCityThreeName] = useState<string | null | undefined>(undefined);
  const [cityThreeShare, setCityThreeShare] = useState<number | null | undefined>(undefined);
  const [countryOneName, setCountryOneName] = useState<string | null | undefined>(undefined);
  const [countryOneShare, setCountryOneShare] = useState<number | null | undefined>(undefined);
  const [countryTwoName, setCountryTwoName] = useState<string | null | undefined>(undefined);
  const [countryTwoShare, setCountryTwoShare] = useState<number | null | undefined>(undefined);
  const [countryThreeName, setCountryThreeName] = useState<string | null | undefined>(undefined);
  const [countryThreeShare, setCountryThreeShare] = useState<number | null | undefined>(undefined);
  const [ageGroupOneShare, setAgeGroupOneShare] = useState<number | null | undefined>(undefined);
  const [ageGroupTwoShare, setAgeGroupTwoShare] = useState<number | null | undefined>(undefined);
  const [ageGroupThreeShare, setAgeGroupThreeShare] = useState<number | null | undefined>(undefined);
  const [maleRatio, setMaleRatio] = useState<number | null | undefined>(undefined);
  const [femaleRatio, setFemaleRatio] = useState<number | null | undefined>(undefined);
  const [insightsSuccess, setInsightsSuccess] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(true);
  const [personalInfoCheck, setPersonalInfoCheck] = useState(false);
  const [instagramCheck, setInstagramCheck] = useState(false);
  const [insightsCheck, setInsightsCheck] = useState(false);
  const [personalDetailCheck, setPersonalDetailCheck] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshErrorMessage, setRefreshErrorMessage] = useState(false);
  const [showReasonOptions, setShowReasonOptions] = useState(false);
  const [showTiktokReasonOptions, setShowTiktokReasonOptions] = useState(false);
  const [ugcVerifications, setUgcVerifications] = useState<UGCVideoVerificationSerializer[]>([]);
  const [reason, setReason] = useState("7");
  const [tiktokReason, setTiktokReason] = useState("7");
  const [accountErrors, setAccountErrors] = useState<string[]>([]);
  const [instagramErrors, setInstagramErrors] = useState<string[]>([]);
  const [personalErrors, setPersonalErrors] = useState<string[]>([]);
  const [insightsErrors, setInsightsErrors] = useState<string[]>([]);

  const verificationUserId = influencerDetails?.user.id!;
  const {isPending: isRejectingInstagram, mutate: rejectInstagram} = useRejectInstagramMutation(verificationUserId);
  const {isPending: isRejectingTiktok, mutate: rejectTiktok} = useRejectTiktokMutation(verificationUserId);
  const {isPending: isVerifyingInstagram, mutate: verifyInstagram} = useVerifyInstagramMutation(verificationUserId);
  const {isPending: isVerifyingTiktok, mutate: verifyTiktok} = useVerifyTiktokMutation(verificationUserId);
  const {isPending: isVerifyingUgc, mutate: verifyUgc} = useVerifyUgcMutation(verificationUserId);

  // Account deletion
  const [confirm, setConfirm] = useState(false);

  const getRejectionReasonLabel = ({reason}: {reason: string}) => {
    const key = REJECTION_SMS_REASON_TO_KEY[reason as keyof typeof REJECTION_SMS_REASON_TO_KEY];
    return key ? t(key) : reason;
  };

  const handleDelete = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    client.defaults.xsrfCookieName = "csrftoken";
    client.defaults.xsrfHeaderName = "X-CSRFTOKEN";
    client
      .get(`/influencer/${influencerId}/delete_account/`)
      .then(() => {
        window.location.reload();
      })
      .catch(error => {
        console.log(error);
      });
  };

  const navigate = Route.useNavigate();
  const {influencerId} = Route.useParams();

  const userData = useUserStore(state => state.user);

  const redirectToDetail = () => {
    navigate({to: "/influencer/$influencerId", params: {influencerId}});
  };

  useEffect(() => {
    if (username && followers && engagement) {
      setInstagramCheck(true);
    }
  }, [engagement, followers, username]);

  // Only the verifications are refreshed here: re-running fillPersonalAccountData would
  // overwrite whatever the manager is currently typing into the account form.
  useEffect(() => {
    if (!ugcVerifications.some(verification => verification.ai_evaluation?.status === "running")) {
      return;
    }

    const intervalId = setInterval(async () => {
      try {
        const response = await client.get(`/influencer/${influencerId}/`);
        setUgcVerifications(response.data.ugc_video_verifications ?? []);
      } catch (error) {
        console.log("Error refreshing UGC AI evaluations", error);
      }
    }, 10000);

    return () => clearInterval(intervalId);
  }, [influencerId, ugcVerifications]);

  useEffect(() => {
    if (
      email &&
      firstName &&
      lastName &&
      city &&
      postalCode &&
      street &&
      (streetNumber || !isStreetNumberRequired(country)) &&
      country &&
      phoneNumber &&
      gender &&
      bio
    ) {
      setPersonalInfoCheck(true);
    }
  }, [bio, city, country, email, firstName, gender, lastName, phoneNumber, postalCode, street, streetNumber]);

  useEffect(() => {
    if (top && below && shoeSize && personalStyle) {
      setPersonalDetailCheck(true);
    }
  }, [below, personalStyle, shoeSize, top]);

  useEffect(() => {
    if (
      cityOneName &&
      cityOneShare &&
      cityTwoName &&
      cityTwoShare &&
      cityThreeName &&
      cityThreeShare &&
      countryOneName &&
      countryOneShare &&
      countryTwoName &&
      countryTwoShare &&
      countryThreeName &&
      countryThreeShare &&
      ageGroupOneShare &&
      ageGroupTwoShare &&
      ageGroupThreeShare &&
      maleRatio &&
      femaleRatio
    ) {
      setInsightsCheck(true);
    }
  }, [
    ageGroupOneShare,
    ageGroupThreeShare,
    ageGroupTwoShare,
    cityOneName,
    cityOneShare,
    cityThreeName,
    cityThreeShare,
    cityTwoName,
    cityTwoShare,
    countryOneName,
    countryOneShare,
    countryThreeName,
    countryThreeShare,
    countryTwoName,
    countryTwoShare,
    femaleRatio,
    maleRatio,
  ]);

  const fillPersonalAccountData = (response: {
    data: UserSerializer & {
      tiktok_verified: boolean;
      bank_account?: UserBankAccountSerializer;
      latest_verification?: LatestVerificationSerializer | null;
    };
  }) => {
    setBankAccount(response.data.bank_account);
    setEmail(response.data.email);
    setFirstName(response.data.first_name);
    setLastName(response.data.last_name);
    setStreet(response.data.address_street);
    setStreetNumber(response.data.address_street_number);
    setPostalCode(response.data.address_postal);
    setCity(response.data.address_city);
    setCountry(response.data.country);
    setPhoneNumber(response.data.phonenumber);
    setGender(response.data.sex)!;
    setBio(response.data.aboutme);
    setIsInstaVerified(response.data.is_verified!);
    setIsTiktokVerified(response.data.tiktok_verified);
    setTiktokRejectionSmsReason(response.data.tiktok_rejection_sms_reason);
    setRejectionSmsReason(response.data.rejection_sms_reason);
    setLatestVerification(response.data.latest_verification ?? null);
  };

  const fillAccountData = (response: {data: InfluencerProfileDetailSerializer}) => {
    setDateOfBirth(response.data.date_of_birth);
    setTop(response.data.top);
    setBelow(response.data.below);
    setShoeSize(response.data.shoe_size);
    setPersonalStyle(response.data.personal_style);
    setUgcVerifications(response.data.ugc_video_verifications!);
    setIsApplicationRestricted(response.data.application_restricted ?? false);
    setIsInstaPro(response.data.user.is_instagram_pro ?? false);
    setIsTikTokPro(response.data.user.is_tiktok_pro ?? false);
  };

  const fillInstagramData = (response: {data: InfluencerProfileDetailSerializer}) => {
    setUsername(response.data.user.username);
    setOriginalUsername(response.data.user.username);
    setFollowers(response.data.followers);
    setEngagement(response.data.engagement);
  };

  const fillInsightsData = (response: {data: InfluencerProfileDetailSerializer}) => {
    setCityOneName(response.data.city1_name);
    setCityOneShare(response.data.city1_share);
    setCityTwoName(response.data.city2_name);
    setCityTwoShare(response.data.city2_share);
    setCityThreeName(response.data.city3_name);
    setCityThreeShare(response.data.city3_share);
    setCountryOneName(response.data.country1_name);
    setCountryOneShare(response.data.country1_share);
    setCountryTwoName(response.data.country2_name);
    setCountryTwoShare(response.data.country2_share);
    setCountryThreeName(response.data.country3_name);
    setCountryThreeShare(response.data.country3_share);
    setAgeGroupOneShare(response.data.agegroup1_share);
    setAgeGroupTwoShare(response.data.agegroup2_share);
    setAgeGroupThreeShare(response.data.agegroup3_share);
    setMaleRatio(response.data.male_ratio);
    setFemaleRatio(response.data.female_ratio);
  };

  useEffect(() => {
    if (userData) {
      if (userId === influencerDetails?.user.id || userData.influenced_admin === true) {
        setPermissionDenied(false);
      }
    }
  }, [userId, influencerDetails?.user, userData]);

  useEffect(() => {
    client
      .get(`/influencer/${influencerId}/`)
      .then(response => {
        fillAccountData(response);
        fillInstagramData(response);
        fillInsightsData(response);
        setInfluencerDetails(response.data);
        client
          .get(`/users/${response.data.user.id}/`)
          .then(response => {
            fillPersonalAccountData(response);
          })
          .catch(() => {
            console.log("Error loading influencer");
          });
        setLoading(false);
        setErrorMessage("");
      })
      .catch(error => {
        if (error.response?.status === 403) {
          setPermissionDenied(true);
        } else {
          setErrorMessage(t("influencer_profile:could_not_load_influencer_details"));
        }
      });
  }, [influencerId]);

  const updateInstagram = () => {
    client.defaults.xsrfCookieName = "csrftoken";
    client.defaults.xsrfHeaderName = "X-CSRFTOKEN";
    client
      .post(`/influencer/${influencerDetails?.uuid}/update_instagram/`, {
        username: username,
        followers: followers,
        engagement: engagement,
      })
      .then(() => {
        setInstagramErrors([]);
        setInstagramSuccess(true);
        redirectToDetail();
      })
      .catch(error => {
        setInstagramSuccess(false);
        setInstagramErrors(getResponseErrorMessages(error));
      });
  };

  const handleInstagramUpdate = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    // if username has not changed, no need to validate username
    if (username === originalUsername) {
      updateInstagram();
      return;
    }
    client.defaults.xsrfCookieName = "csrftoken";
    client.defaults.xsrfHeaderName = "X-CSRFTOKEN";
    client
      .post("/users/validate-username/", {
        username: username,
      })
      .then(() => {
        setInstagramErrors([]);
        updateInstagram();
      })
      .catch(error => {
        setInstagramErrors(getResponseErrorMessages(error));
      });
  };
  const handlePersonalStyleUpdate = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    client.defaults.xsrfCookieName = "csrftoken";
    client.defaults.xsrfHeaderName = "X-CSRFTOKEN";
    client
      .patch(`/influencer/${influencerDetails?.uuid}/`, {
        top: top,
        below: below,
        shoe_size: shoeSize,
        personal_style: personalStyle,
      })
      .then(() => {
        setPersonalErrors([]);
        setPersonalSuccess(true);
        redirectToDetail();
      })
      .catch(error => {
        setPersonalSuccess(false);
        setPersonalErrors(getResponseErrorMessages(error));
      });
  };

  const cleanPercentage = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return null;
    }
    return value.toString().replace("%", "");
  };

  const handleInsightsUpdate = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    client.defaults.xsrfCookieName = "csrftoken";
    client.defaults.xsrfHeaderName = "X-CSRFTOKEN";
    client
      .patch(`/influencer/${influencerDetails?.uuid}/`, {
        male_ratio: cleanPercentage(maleRatio),
        female_ratio: cleanPercentage(femaleRatio),
        city1_name: cityOneName,
        city1_share: cleanPercentage(cityOneShare),
        city2_name: cityTwoName,
        city2_share: cleanPercentage(cityTwoShare),
        city3_name: cityThreeName,
        city3_share: cleanPercentage(cityThreeShare),
        country1_name: countryOneName,
        country1_share: cleanPercentage(countryOneShare),
        country2_name: countryTwoName,
        country2_share: cleanPercentage(countryTwoShare),
        country3_name: countryThreeName,
        country3_share: cleanPercentage(countryThreeShare),
        agegroup1_share: cleanPercentage(ageGroupOneShare),
        agegroup2_share: cleanPercentage(ageGroupTwoShare),
        agegroup3_share: cleanPercentage(ageGroupThreeShare),
      })
      .then(() => {
        setInsightsErrors([]);
        setInsightsSuccess(true);
        redirectToDetail();
      })
      .catch(error => {
        setInsightsSuccess(false);
        setInsightsErrors(getResponseErrorMessages(error));
      });
  };

  const handleUpdateFollowersEngagement = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    client.defaults.xsrfCookieName = "csrftoken";
    client.defaults.xsrfHeaderName = "X-CSRFTOKEN";
    client
      .post(`/influencer/${influencerDetails?.uuid}/update_followers_engagement/`, {})
      .then(response => {
        fillInstagramData(response);
        setRefreshErrorMessage(false);
      })
      .catch(() => {
        setRefreshErrorMessage(true);
        console.log("Error updating influencer followers and engagement");
      });
  };

  if (loading === true) {
    return <FullPageLoader />;
  }

  if (permissionDenied === true) {
    return <PermissionDenied />;
  }

  if (errorMessage !== "") {
    return (
      <div className="text-neutral-black mx-auto flex h-[60vh] max-w-212.5 flex-col items-start justify-center gap-2 px-10">
        <span className="text-4xl font-medium">{errorMessage ?? t("unknownError")}</span>
        <span className="text-lg font-medium">
          {t("Please try again later. If the problem persists, please contact support")}
        </span>
      </div>
    );
  }

  return (
    <>
      <meta name="robots" content="noindex" />
      <div className="max-w-wide relative self-center px-5 py-8 md:px-8">
        <div className="relative z-20 mx-auto">
          <h1 className="m-0">{t("Settings")}</h1>
          <div className="flex flex-col sm:flex-row">
            <div className="mr-5 basis-[30%] lg:basis-[20%]">
              <div className="top-30 sm:sticky">
                <p>
                  <a href="#Account">{t("account")}</a>
                </p>
                {isAdmin && (
                  <p>
                    <a href="#Wallet">{t("influencer_profile:wallet_activity")}</a>
                  </p>
                )}
                <p>
                  <a href="#Instagram">{t("Instagram")}</a>
                </p>
                <p>
                  <a href="#Insights">{t("Insights")}</a>
                </p>
              </div>
            </div>
            <div className="flex-1">
              <div className="mb-16 flex flex-col items-start rounded-3xl bg-white p-5 shadow-lg">
                {personalInfoCheck && (
                  <div className="flex items-center">
                    <div className="bg-bold-blue-300 relative mr-2.5 mb-2.5 inline-block min-h-10 min-w-10 cursor-pointer rounded-2xl p-2">
                      <img src={TickIcon} loading="lazy" alt="" className="inline-block size-6 shrink-0 rounded-none" />
                    </div>
                    <p>{t("influencer_profile:personal_info_complete")}</p>
                  </div>
                )}
                {!personalInfoCheck && (
                  <div className="flex items-center">
                    <div className="relative mr-2.5 mb-2.5 inline-block min-h-10 min-w-10 cursor-pointer rounded-2xl bg-red-300 p-2">
                      <InfoIcon className="inline-block size-6 shrink-0 rounded-none" />
                    </div>
                    <p>{t("influencer_profile:personal_info_incomplete")}</p>
                  </div>
                )}
                <div>
                  <div className="mb-4 h-0 overflow-hidden">
                    <label>{t("name")}</label>
                  </div>
                  <h2 className="-mt-3 mb-2">{t("account")}</h2>
                </div>
                <div className="mb-4 w-full">
                  <form className="-mx-2.5 flex flex-wrap" onSubmit={e => e.preventDefault()}>
                    {isAdmin && (
                      <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap lg:basis-[45%]">
                        <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                          {t("influencer_profile:influencer_uuid")}
                        </label>
                        <input
                          type="text"
                          className="bg-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl p-2 text-gray-500 shadow-xs"
                          placeholder={t("influencer_profile:influencer_uuid")}
                          value={influencerDetails?.uuid}
                          disabled
                        />
                      </div>
                    )}
                    {isAdmin && (
                      <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap lg:basis-[45%]">
                        <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                          {t("influencer_profile:user_id")}
                        </label>
                        <input
                          type="text"
                          className="bg-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl p-2 text-gray-500 shadow-xs"
                          placeholder={t("influencer_profile:user_id")}
                          value={influencerDetails?.user.id}
                          disabled
                        />
                      </div>
                    )}
                    <div className="mx-2.5 mb-4 w-full">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("email")}
                      </label>
                      {isAdmin ? (
                        <input
                          className={clsxm(
                            "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                            {
                              "border-red-300": !email,
                            },
                          )}
                          placeholder={t("email")}
                          value={email}
                          onChange={event => setEmail(event.target.value)}
                        />
                      ) : (
                        <h1 className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1">
                          {email}
                        </h1>
                      )}
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap lg:basis-[45%]">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:first_name")}
                      </label>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !firstName,
                          },
                        )}
                        maxLength={256}
                        placeholder={t("influencer_profile:first_name")}
                        value={firstName}
                        onChange={event => setFirstName(event.target.value)}
                      />
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap lg:basis-[45%]">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:last_name")}
                      </label>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !lastName,
                          },
                        )}
                        maxLength={256}
                        placeholder={t("influencer_profile:last_name")}
                        value={lastName}
                        onChange={event => setLastName(event.target.value)}
                      />
                    </div>
                    <div className="mx-2.5 mb-4 w-full">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:address")}
                      </label>
                      <p className="mb-1.5 text-xs text-black">{t("influencer_profile:address_privacy_note")}</p>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !street,
                          },
                        )}
                        maxLength={256}
                        placeholder={t("influencer_profile:street")}
                        value={street ?? ""}
                        onChange={event => setStreet(event.target.value)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !streetNumber && isStreetNumberRequired(country),
                          },
                        )}
                        maxLength={256}
                        placeholder={t("influencer_profile:house_number")}
                        value={streetNumber ?? ""}
                        onChange={event => setStreetNumber(event.target.value)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !postalCode,
                          },
                        )}
                        maxLength={256}
                        placeholder={t("influencer_profile:postal_code")}
                        value={postalCode ?? ""}
                        onChange={event => setPostalCode(event.target.value)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !city,
                          },
                        )}
                        maxLength={256}
                        placeholder={t("city")}
                        value={city ?? ""}
                        onChange={event => setCity(event.target.value)}
                      />
                      <select
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !country,
                          },
                        )}
                        value={country ?? ""}
                        onChange={event => setCountry(event.target.value)}>
                        <option value="Switzerland" key="switzerland">
                          {t("Switzerland")}
                        </option>
                        <option value="Liechtenstein" key="liechtenstein">
                          {t("Liechtenstein")}
                        </option>
                        <option value="Germany" key="germany">
                          {t("Germany")}
                        </option>
                        <option value="Austria" key="austria">
                          {t("Austria")}
                        </option>
                        <option value="Italy" key="italy">
                          {t("Italy")}
                        </option>
                        <option value="France" key="france">
                          {t("France")}
                        </option>
                        <option value="United States" key="us">
                          {t("United States")}
                        </option>
                        <option value="United Kingdom" key="uk">
                          {t("United Kingdom")}
                        </option>
                      </select>
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap lg:basis-[45%]">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:phone_number")}
                      </label>
                      <p className="mb-1.5 text-xs text-black">{t("influencer_profile:phone_privacy_note")}</p>
                      <PhoneInput
                        value={phoneNumber ?? ""}
                        showLabel={false}
                        defaultCountry="CH"
                        onChange={setPhoneNumber}
                        buttonClassName="h-9 shadow-highlight rounded-l-2xl"
                        numberInputClassName="h-9 shadow-highlight rounded-r-2xl"
                      />
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap lg:basis-[45%]">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("Gender")}
                      </label>
                      <select
                        className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1"
                        value={gender ?? ""}
                        onChange={event => setGender(event.target.value)}>
                        <option value="Weiblich" key="female">
                          {t("female")}
                        </option>
                        <option value="Männlich" key="male">
                          {t("male")}
                        </option>
                        <option value="diverse" key="diverse">
                          {t("Diverse")}
                        </option>
                        <option value="Keine Angabe" key="prefer">
                          {t("Prefer not to say")}
                        </option>
                      </select>
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap lg:basis-[45%]">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:date_of_birth")}
                      </label>
                      <input
                        value={dateOfBirth ?? ""}
                        onChange={event => setDateOfBirth(event.target.value)}
                        type="date"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !dateOfBirth,
                          },
                        )}
                      />
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap lg:basis-[45%]">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:bio")}
                      </label>
                      <textarea
                        value={bio ?? ""}
                        onChange={event => setBio(event.target.value)}
                        placeholder={t("influencer_profile:bio")}
                        maxLength={5000}
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !bio,
                          },
                        )}
                      />
                    </div>
                    {isAdmin && bankAccount && (
                      <div className="mx-2.5 mb-6 flex w-full flex-col">
                        <span className="text-neutral-black mb-2 text-sm font-semibold">
                          {t("influencer_profile:bank_account_information")}
                        </span>
                        <div className="flex flex-col gap-4 lg:flex-row">
                          <div className="flex w-full flex-col flex-wrap">
                            <label className="text-good-grey-500 block text-xs font-semibold tracking-wider uppercase">
                              {t("influencer_profile:first_name")}
                            </label>
                            <input
                              type="text"
                              className="bg-good-grey-100 block w-full resize-y rounded-2xl p-2 text-gray-500 shadow-xs"
                              value={bankAccount.first_name}
                              disabled
                            />
                          </div>
                          <div className="flex w-full flex-col flex-wrap">
                            <label className="text-good-grey-500 block text-xs font-semibold tracking-wider uppercase">
                              {t("influencer_profile:last_name")}
                            </label>
                            <input
                              type="text"
                              className="bg-good-grey-100 block w-full resize-y rounded-2xl p-2 text-gray-500 shadow-xs"
                              value={bankAccount.last_name}
                              disabled
                            />
                          </div>
                        </div>
                        <div className="flex w-full flex-col flex-wrap">
                          <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                            {t("influencer_profile:iban")}
                          </label>
                          <input
                            type="text"
                            className="bg-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl p-2 text-gray-500 shadow-xs"
                            value={bankAccount.iban}
                            disabled
                          />
                        </div>
                      </div>
                    )}
                    {isAdmin && (
                      <div className="mx-2.5 mb-10 flex w-full items-center gap-2">
                        <input type="checkbox" id="application-restricted" checked={isApplicationRestricted} disabled />
                        <label htmlFor="application-restricted" className="text-neutral-grey-dark mb-0 text-sm">
                          {t("influencer_profile:is_application_restricted")}
                        </label>
                      </div>
                    )}
                    {isAdmin && (
                      <div className="mx-2.5 mb-10 flex w-full flex-col gap-3">
                        <div className="flex w-full flex-wrap items-center gap-5">
                          <div className="flex flex-col gap-2">
                            <span className="text-neutral-black text-sm font-semibold">
                              {t("influencer_profile:pro_instagram")}
                            </span>
                            <Toggle checked={isInstaPro} onChange={() => setIsInstaPro(!isInstaPro)} />
                          </div>
                          <div className="flex flex-col gap-2">
                            <span className="text-neutral-black text-sm font-semibold">
                              {t("influencer_profile:pro_tiktok")}
                            </span>
                            <Toggle
                              checked={isTikTokPro}
                              onChange={() => setIsTikTokPro(!isTikTokPro)}
                              disabled={!influencerDetails?.user.tiktok_username}
                            />
                          </div>
                        </div>
                        {!influencerDetails?.user.tiktok_username && (
                          <span className="text-warning-medium-yellow inline-flex items-center gap-2 text-sm font-semibold">
                            <InfoIcon className="size-4 shrink-0" />
                            {t("influencer_profile:pro_tiktok_disabled_no_connection")}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="mx-2.5 mb-6 w-full">
                      <button
                        type="button"
                        className="bg-bold-blue-500 hover:shadow-highlight mr-2.5 min-h-10 min-w-10 cursor-pointer rounded-2xl border-none px-5 py-3 text-center font-medium tracking-wider text-white uppercase hover:scale-105"
                        onClick={async () => {
                          try {
                            if (influencerDetails?.user.is_instagram_pro !== isInstaPro) {
                              await client.post(`/users/${influencerDetails?.user.id}/mark_pro/`, {
                                social_platform: "instagram",
                              });
                              setInfluencerDetails(
                                current =>
                                  current && {...current, user: {...current.user, is_instagram_pro: isInstaPro}},
                              );
                            }

                            if (influencerDetails?.user.is_tiktok_pro !== isTikTokPro) {
                              await client.post(`/users/${influencerDetails?.user.id}/mark_pro/`, {
                                social_platform: "tiktok",
                              });
                              setInfluencerDetails(
                                current => current && {...current, user: {...current.user, is_tiktok_pro: isTikTokPro}},
                              );
                            }

                            await client.patch(`/users/${influencerDetails?.user.id}/`, {
                              email: email,
                              first_name: firstName,
                              last_name: lastName,
                              address_street: street,
                              address_street_number: streetNumber,
                              address_postal: postalCode,
                              address_city: city,
                              country: country,
                              phonenumber: phoneNumber,
                              sex: gender,
                              date_of_birth: dayjs(dateOfBirth).isValid()
                                ? dayjs(dateOfBirth).format("YYYY-MM-DD")
                                : undefined,
                              aboutme: bio,
                              type: "influencer",
                            });
                            setAccountErrors([]);
                            setAccountSuccess(true);
                            redirectToDetail();
                          } catch (error) {
                            console.error("Error updating influencer:", error);
                            setAccountSuccess(false);
                            setAccountErrors(getResponseErrorMessages(error));
                          }
                        }}>
                        {t("Save")}
                      </button>
                      <FormErrors errors={accountErrors} />
                    </div>
                  </form>
                  {accountSuccess && (
                    <div>
                      <div>{t("influencer_profile:account_details_updated")}</div>
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div className="border-neutral-white-dark mb-6 w-full border-t pt-6">
                    <h3 className="text-neutral-black mb-3 text-sm font-semibold">
                      {t("influencer_profile:verifications")}
                    </h3>
                    <div className="divide-neutral-white-dark border-neutral-white-dark flex flex-col divide-y rounded-2xl border px-4">
                      <VerificationChannelRow
                        icon={<InstagramIcon className="size-4 shrink-0" />}
                        label={t("influencer_profile:instagram")}
                        detail={
                          <>
                            {influencerDetails?.user.username && (
                              <span className="text-neutral-grey-dark truncate text-xs">
                                @{influencerDetails.user.username}
                              </span>
                            )}
                            {!isInstaVerified && rejectionSmsReason && (
                              <span className="text-danger-medium-red text-xs">
                                {getRejectionReasonLabel({reason: rejectionSmsReason})}
                              </span>
                            )}
                            <VerificationSourceNote source={latestVerification?.instagram} />
                          </>
                        }
                        status={
                          <VerificationStatusPill
                            label={t(
                              isInstaVerified
                                ? "influencer_profile:verification_status_verified"
                                : rejectionSmsReason
                                  ? "influencer_profile:verification_status_rejected"
                                  : "influencer_profile:verification_status_not_verified",
                            )}
                            tone={isInstaVerified ? "verified" : rejectionSmsReason ? "rejected" : "neutral"}
                          />
                        }
                        actions={
                          <>
                            <Button
                              buttonStyle={isInstaVerified ? "White" : "Blue"}
                              className="h-9 px-3.5 text-xs"
                              isDisabled={isRejectingInstagram}
                              isLoading={isVerifyingInstagram}
                              onClick={() =>
                                verifyInstagram({verify: !isInstaVerified}, {onSuccess: fillPersonalAccountData})
                              }>
                              {isInstaVerified
                                ? t("influencer_profile:unverify_instagram")
                                : t("influencer_profile:verify_instagram")}
                            </Button>
                            {!isInstaVerified && !rejectionSmsReason && !showReasonOptions && (
                              <Button
                                buttonStyle="White"
                                className="h-9 px-3.5 text-xs"
                                isDisabled={isVerifyingInstagram}
                                onClick={() => setShowReasonOptions(true)}>
                                {t("influencer_profile:reject_instagram_with_reason")}
                              </Button>
                            )}
                          </>
                        }>
                        {!isInstaVerified && !rejectionSmsReason && showReasonOptions && (
                          <VerificationRejectPanel
                            confirmLabel={t("influencer_profile:confirm_instagram_rejection")}
                            isConfirming={isRejectingInstagram}
                            onCancel={() => setShowReasonOptions(false)}
                            onConfirm={() => rejectInstagram({reason}, {onSuccess: fillPersonalAccountData})}
                            onReasonChange={setReason}
                            reason={reason}
                            reasons={INSTAGRAM_REJECTION_REASONS.map(({labelKey, value}) => ({
                              label: t(labelKey),
                              value,
                            }))}
                          />
                        )}
                      </VerificationChannelRow>
                      <VerificationChannelRow
                        icon={<TiktokIcon className="size-4 shrink-0" />}
                        label={t("influencer_profile:tiktok")}
                        detail={
                          <>
                            {influencerDetails?.user.tiktok_username && (
                              <span className="text-neutral-grey-dark truncate text-xs">
                                @{influencerDetails.user.tiktok_username}
                              </span>
                            )}
                            {!isTiktokVerified && tiktokRejectionSmsReason && (
                              <span className="text-danger-medium-red text-xs">
                                {getRejectionReasonLabel({reason: tiktokRejectionSmsReason})}
                              </span>
                            )}
                            <VerificationSourceNote source={latestVerification?.tiktok} />
                          </>
                        }
                        status={
                          <VerificationStatusPill
                            label={t(
                              isTiktokVerified
                                ? "influencer_profile:verification_status_verified"
                                : tiktokRejectionSmsReason
                                  ? "influencer_profile:verification_status_rejected"
                                  : "influencer_profile:verification_status_not_verified",
                            )}
                            tone={isTiktokVerified ? "verified" : tiktokRejectionSmsReason ? "rejected" : "neutral"}
                          />
                        }
                        actions={
                          <>
                            <Button
                              buttonStyle={isTiktokVerified ? "White" : "Blue"}
                              className="h-9 px-3.5 text-xs"
                              isDisabled={isRejectingTiktok}
                              isLoading={isVerifyingTiktok}
                              onClick={() => verifyTiktok(undefined, {onSuccess: fillPersonalAccountData})}>
                              {isTiktokVerified
                                ? t("influencer_profile:unverify_tiktok")
                                : t("influencer_profile:verify_tiktok")}
                            </Button>
                            {!isTiktokVerified && !tiktokRejectionSmsReason && !showTiktokReasonOptions && (
                              <Button
                                buttonStyle="White"
                                className="h-9 px-3.5 text-xs"
                                isDisabled={isVerifyingTiktok}
                                onClick={() => setShowTiktokReasonOptions(true)}>
                                {t("influencer_profile:reject_tiktok_with_reason")}
                              </Button>
                            )}
                          </>
                        }>
                        {!isTiktokVerified && !tiktokRejectionSmsReason && showTiktokReasonOptions && (
                          <VerificationRejectPanel
                            confirmLabel={t("influencer_profile:confirm_tiktok_rejection")}
                            isConfirming={isRejectingTiktok}
                            onCancel={() => setShowTiktokReasonOptions(false)}
                            onConfirm={() => rejectTiktok({reason: tiktokReason}, {onSuccess: fillPersonalAccountData})}
                            onReasonChange={setTiktokReason}
                            reason={tiktokReason}
                            reasons={TIKTOK_REJECTION_REASONS.map(({labelKey, value}) => ({
                              label: t(labelKey),
                              value,
                            }))}
                          />
                        )}
                      </VerificationChannelRow>
                      <VerificationChannelRow
                        icon={<UgcIcon className="h-4 w-5 shrink-0" />}
                        label={t("influencer_profile:ugc")}
                        status={
                          <VerificationStatusPill
                            label={t(
                              influencerDetails?.user.ugc_verified
                                ? "influencer_profile:verification_status_verified"
                                : "influencer_profile:verification_status_not_verified",
                            )}
                            tone={influencerDetails?.user.ugc_verified ? "verified" : "neutral"}
                          />
                        }
                        actions={
                          <Button
                            buttonStyle={influencerDetails?.user.ugc_verified ? "White" : "Blue"}
                            className="h-9 px-3.5 text-xs"
                            isLoading={isVerifyingUgc}
                            onClick={() =>
                              verifyUgc(undefined, {
                                onSuccess: () =>
                                  setInfluencerDetails(
                                    current =>
                                      current && {
                                        ...current,
                                        user: {...current.user, ugc_verified: !current.user.ugc_verified},
                                      },
                                  ),
                              })
                            }>
                            {influencerDetails?.user.ugc_verified
                              ? t("influencer_profile:unverify_ugc")
                              : t("influencer_profile:verify_ugc")}
                          </Button>
                        }>
                        {ugcVerifications.length > 0 && (
                          <div className="flex w-full flex-col gap-4">
                            {ugcVerifications.map(ugcVerification => (
                              <UGCVerification
                                key={ugcVerification.id}
                                ugcVerification={ugcVerification}
                                ugcVerified={influencerDetails?.user.ugc_verified ?? false}
                              />
                            ))}
                          </div>
                        )}
                      </VerificationChannelRow>
                    </div>
                  </div>
                )}
                <Can I="create" a="DisciplinaryOperation">
                  {influencerDetails?.user.id && (
                    <div className="border-neutral-white-dark col mb-5 flex w-full flex-col border-t pt-6">
                      <div className="flex items-center gap-3">
                        <Button
                          buttonStyle="White"
                          className="h-fit gap-2 p-3"
                          onClick={() => {
                            initializeDisciplineModal({influencer: influencerDetails});
                            setDisciplinaryModalOpen(true);
                          }}>
                          <HammerIcon className="size-3.5 shrink-0" />
                          <span>{t("influencer_discipline:disciplinary_action")}</span>
                        </Button>
                        <DisciplineIndicators influencer={influencerDetails} showEmptyState showUnverifications />
                      </div>
                      <DisciplineHistory userId={influencerDetails?.user.id} />
                    </div>
                  )}
                </Can>
              </div>
              {isAdmin && !!influencerDetails?.uuid && (
                <WalletActivity
                  cashEarned={influencerDetails.cash_earned}
                  currency={influencerDetails.user.currency}
                  influencerId={influencerDetails.uuid}
                />
              )}
              <ChangePasswordSection />
              <div className="mb-16 flex flex-col items-start rounded-3xl bg-white p-5 shadow-lg">
                {instagramCheck && (
                  <div className="flex items-center">
                    <div className="bg-bold-blue-300 relative mr-2.5 mb-2.5 inline-block min-h-10 min-w-10 cursor-pointer rounded-2xl p-2">
                      <img src={TickIcon} loading="lazy" alt="" className="inline-block size-6 shrink-0 rounded-none" />
                    </div>
                    <p>{t("influencer_profile:personal_info_complete")}</p>
                  </div>
                )}
                {!instagramCheck && (
                  <div className="flex items-center">
                    <div className="relative mr-2.5 mb-2.5 inline-block min-h-10 min-w-10 cursor-pointer rounded-2xl bg-red-300 p-2">
                      <InfoIcon className="inline-block size-6 shrink-0 rounded-none" />
                    </div>
                    <p>{t("influencer_profile:personal_info_incomplete")}</p>
                  </div>
                )}
                <div>
                  <div className="mb-4 h-0 overflow-hidden">
                    <label>{t("name")}</label>
                  </div>
                  <h2 className="-mt-3 mb-2">{t("Instagram")}</h2>
                </div>
                <div className="mb-4 w-full">
                  <form className="-mx-2.5 flex flex-wrap">
                    <div className="mx-2.5 mb-4 w-full">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:instagram_username")}
                      </label>
                      {isAdmin ? (
                        <input
                          className={clsxm(
                            "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                            {
                              "border-red-300": !username,
                            },
                          )}
                          value={username ?? ""}
                          onChange={event => setUsername(event.target.value)}
                        />
                      ) : (
                        <h1 className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1">
                          {username}
                        </h1>
                      )}
                    </div>
                    <div className="mx-2.5 mb-6 flex grow flex-col flex-wrap">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:instagram_followers")}
                      </label>
                      <p className="mb-1.5 text-xs text-black">
                        {t("influencer_profile:follower_count_filled_by_refluenced")}
                      </p>
                      {isAdmin ? (
                        <input
                          type="number"
                          className={clsxm(
                            "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                            {
                              "border-red-300": !followers,
                            },
                          )}
                          maxLength={256}
                          placeholder={t("influencer_profile:follower_count_placeholder")}
                          value={followers ?? ""}
                          onChange={event => setFollowers(event.target.value ? +event.target.value : undefined)}
                        />
                      ) : (
                        <h1 className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1">
                          {followers}
                        </h1>
                      )}
                    </div>
                    <div className="mx-2.5 mb-6 flex grow flex-col flex-wrap">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:engagement_rate_percent")}
                      </label>
                      <p className="mb-1.5 text-xs text-black">
                        {t("influencer_profile:engagement_rate_calculated_by_refluenced")}
                      </p>
                      {isAdmin ? (
                        <input
                          type="number"
                          className={clsxm(
                            "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                            {
                              "border-red-300": !engagement,
                            },
                          )}
                          maxLength={256}
                          placeholder="%"
                          value={engagement ?? ""}
                          onChange={event => setEngagement(event.target.value ? +event.target.value : undefined)}
                        />
                      ) : (
                        <h1 className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1">
                          {engagement}
                        </h1>
                      )}
                    </div>
                    {isAdmin && (
                      <>
                        <div className="mx-2.5 mb-4">
                          <p>
                            <button
                              className="bg-bold-blue-500 hover:shadow-highlight mr-2.5 min-h-10 min-w-10 cursor-pointer rounded-2xl border-none px-5 py-3 text-center font-medium tracking-wider text-white uppercase hover:scale-105"
                              onClick={handleUpdateFollowersEngagement}>
                              {t("influencer_profile:update_followers_and_engagement")}
                            </button>
                          </p>
                        </div>
                        {refreshErrorMessage && <p>{t("influencer_profile:could_not_refresh_followers_engagement")}</p>}
                      </>
                    )}
                  </form>
                  {instagramSuccess && (
                    <div>
                      <div>{t("influencer_profile:instagram_details_updated")}</div>
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <button
                    className="bg-bold-blue-500 hover:shadow-highlight mr-2.5 min-h-10 min-w-10 cursor-pointer rounded-2xl border-none px-5 py-3 text-center font-medium tracking-wider text-white uppercase hover:scale-105"
                    onClick={handleInstagramUpdate}>
                    {t("Save")}
                  </button>
                )}
                <FormErrors errors={instagramErrors} />
              </div>
              <div className="mb-16 flex flex-col items-start rounded-3xl bg-white p-5 shadow-lg">
                {personalDetailCheck && (
                  <div className="flex items-center">
                    <div className="bg-bold-blue-300 relative mr-2.5 mb-2.5 inline-block min-h-10 min-w-10 cursor-pointer rounded-2xl p-2">
                      <img src={TickIcon} loading="lazy" alt="" className="inline-block size-6 shrink-0 rounded-none" />
                    </div>
                    <p>{t("influencer_profile:personal_info_complete")}</p>
                  </div>
                )}
                {!personalDetailCheck && (
                  <div className="flex items-center">
                    <div className="relative mr-2.5 mb-2.5 inline-block min-h-10 min-w-10 cursor-pointer rounded-2xl bg-red-300 p-2">
                      <InfoIcon className="inline-block size-6 shrink-0 rounded-none" />
                    </div>
                    <p>{t("influencer_profile:personal_info_incomplete")}</p>
                  </div>
                )}
                <div>
                  <div className="mb-4 h-0 overflow-hidden">
                    <form>
                      <label>{t("name")}</label>
                    </form>
                  </div>
                  <h2 className="-mt-3 mb-2">{t("influencer_profile:clothing_sizes")}</h2>
                </div>
                <div className="mb-4 w-full">
                  <form className="-mx-2.5 flex flex-wrap">
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap lg:basis-[45%]">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:clothing_sizes_top")}
                      </label>
                      <p className="mb-1.5 text-xs text-black">{t("influencer_profile:clothing_sizes_top_examples")}</p>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !top,
                          },
                        )}
                        maxLength={256}
                        value={top ?? ""}
                        onChange={event => setTop(event.target.value)}
                      />
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap lg:basis-[45%]">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:clothing_sizes_below")}
                      </label>
                      <p className="mb-1.5 text-xs text-black">
                        {t("influencer_profile:clothing_sizes_below_examples")}
                      </p>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !below,
                          },
                        )}
                        maxLength={256}
                        value={below ?? ""}
                        onChange={event => setBelow(event.target.value)}
                      />
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap lg:basis-[45%]">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:clothing_sizes_shoe")}
                      </label>
                      <p className="mb-1.5 text-xs text-black">
                        {t("influencer_profile:clothing_sizes_shoe_examples")}
                      </p>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !shoeSize,
                          },
                        )}
                        maxLength={256}
                        value={shoeSize ?? ""}
                        onChange={event => setShoeSize(event.target.value)}
                      />
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap lg:basis-[45%]">
                      <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:clothing_sizes_personal_style")}
                      </label>
                      <p className="mb-1.5 text-xs text-black">
                        {t("influencer_profile:clothing_sizes_personal_style_examples")}
                      </p>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !personalStyle,
                          },
                        )}
                        maxLength={256}
                        value={personalStyle ?? ""}
                        onChange={event => setPersonalStyle(event.target.value)}
                      />
                    </div>
                  </form>
                  {personalSuccess && (
                    <div>
                      <div>{t("influencer_profile:account_details_updated")}</div>
                    </div>
                  )}
                </div>
                <p className="mb-1.5 text-center text-xs text-black">
                  {t("influencer_profile:clothing_sizes_privacy_note")}
                </p>
                <button
                  className="bg-bold-blue-500 hover:shadow-highlight mr-2.5 min-h-10 min-w-10 cursor-pointer rounded-2xl border-none px-5 py-3 text-center font-medium tracking-wider text-white uppercase hover:scale-105"
                  onClick={handlePersonalStyleUpdate}>
                  {t("Save")}
                </button>
                <FormErrors errors={personalErrors} />
              </div>
              <div className="mb-16 flex flex-col items-start rounded-3xl bg-white p-5 shadow-lg">
                {insightsCheck && (
                  <div className="flex items-center">
                    <div className="bg-bold-blue-300 relative mr-2.5 mb-2.5 inline-block min-h-10 min-w-10 cursor-pointer rounded-2xl p-2">
                      <img src={TickIcon} loading="lazy" alt="" className="inline-block size-6 shrink-0 rounded-none" />
                    </div>
                    <p>{t("influencer_profile:personal_info_complete")}</p>
                  </div>
                )}
                {!insightsCheck && (
                  <div className="flex items-center">
                    <div className="relative mr-2.5 mb-2.5 inline-block min-h-10 min-w-10 cursor-pointer rounded-2xl bg-red-300 p-2">
                      <InfoIcon className="inline-block size-6 shrink-0 rounded-none" />
                    </div>
                    <p>{t("influencer_profile:personal_info_incomplete")}</p>
                  </div>
                )}
                <div>
                  <div className="mb-4 h-0 overflow-hidden">
                    <label>{t("name")}</label>
                  </div>
                  <h2 className="-mt-3 mb-2">{t("Insights")}</h2>
                </div>
                <p>{t("influencer_profile:insights_input_format_note")}</p>
                <div className="mb-4 w-full">
                  <form className="-mx-2.5 flex flex-wrap">
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap self-end sm:basis-[45%] lg:self-start">
                      <h4 className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:top_three_cities")}
                      </h4>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !cityOneName,
                          },
                        )}
                        maxLength={256}
                        placeholder={t("city")}
                        value={cityOneName ?? ""}
                        onChange={event => setCityOneName(event.target.value)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !cityTwoName,
                          },
                        )}
                        maxLength={256}
                        placeholder={t("city")}
                        value={cityTwoName ?? ""}
                        onChange={event => setCityTwoName(event.target.value)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !cityThreeName,
                          },
                        )}
                        maxLength={256}
                        placeholder={t("city")}
                        value={cityThreeName ?? ""}
                        onChange={event => setCityThreeName(event.target.value)}
                      />
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap self-end sm:basis-[45%] lg:self-start">
                      <h4 className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:share")}
                      </h4>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !cityOneShare,
                          },
                        )}
                        maxLength={256}
                        placeholder="50%"
                        value={cityOneShare ?? ""}
                        onChange={event => setCityOneShare(event.target.value ? +event.target.value : undefined)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !cityTwoShare,
                          },
                        )}
                        maxLength={256}
                        placeholder="30%"
                        value={cityTwoShare ?? ""}
                        onChange={event => setCityTwoShare(event.target.value ? +event.target.value : undefined)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !cityThreeShare,
                          },
                        )}
                        maxLength={256}
                        placeholder="20%"
                        value={cityThreeShare ?? ""}
                        onChange={event => setCityThreeShare(event.target.value ? +event.target.value : undefined)}
                      />
                    </div>
                  </form>
                </div>
                <div className="mb-4 w-full">
                  <form className="-mx-2.5 flex flex-wrap">
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap self-end sm:basis-[45%] lg:self-start">
                      <h4 className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:top_three_countries")}
                      </h4>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !countryOneName,
                          },
                        )}
                        maxLength={256}
                        placeholder={t("influencer_profile:country")}
                        value={countryOneName ?? ""}
                        onChange={event => setCountryOneName(event.target.value)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !countryTwoName,
                          },
                        )}
                        maxLength={256}
                        placeholder={t("influencer_profile:country")}
                        value={countryTwoName ?? ""}
                        onChange={event => setCountryTwoName(event.target.value)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !countryThreeName,
                          },
                        )}
                        maxLength={256}
                        placeholder={t("influencer_profile:country")}
                        value={countryThreeName ?? ""}
                        onChange={event => setCountryThreeName(event.target.value)}
                      />
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap self-end sm:basis-[45%] lg:self-start">
                      <h4 className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:share")}
                      </h4>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !countryOneShare,
                          },
                        )}
                        maxLength={256}
                        placeholder="50%"
                        value={countryOneShare ?? ""}
                        onChange={event => setCountryOneShare(event.target.value ? +event.target.value : undefined)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !countryTwoShare,
                          },
                        )}
                        maxLength={256}
                        placeholder="30%"
                        value={countryTwoShare ?? ""}
                        onChange={event => setCountryTwoShare(event.target.value ? +event.target.value : undefined)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !countryThreeShare,
                          },
                        )}
                        maxLength={256}
                        placeholder="20%"
                        value={countryThreeShare ?? ""}
                        onChange={event => setCountryThreeShare(event.target.value ? +event.target.value : undefined)}
                      />
                    </div>
                  </form>
                </div>
                <div className="mb-4 w-full">
                  <form className="-mx-2.5 flex flex-wrap">
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap self-end sm:basis-[45%] lg:self-start">
                      <h4 className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:top_three_age_groups")}
                      </h4>
                      <span className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1">
                        {t("influencer_profile:age_range", {from: 18, to: 24})}
                      </span>
                      <span className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1">
                        {t("influencer_profile:age_range", {from: 25, to: 34})}
                      </span>
                      <span className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1">
                        {t("influencer_profile:age_range", {from: 35, to: 44})}
                      </span>
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap self-end sm:basis-[45%] lg:self-start">
                      <h4 className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:share")}
                      </h4>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !ageGroupOneShare,
                          },
                        )}
                        placeholder="50%"
                        value={ageGroupOneShare ?? ""}
                        onChange={event => setAgeGroupOneShare(event.target.value ? +event.target.value : undefined)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !ageGroupTwoShare,
                          },
                        )}
                        placeholder="30%"
                        value={ageGroupTwoShare ?? ""}
                        onChange={event => setAgeGroupTwoShare(event.target.value ? +event.target.value : undefined)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !ageGroupThreeShare,
                          },
                        )}
                        placeholder="20%"
                        value={ageGroupThreeShare ?? ""}
                        onChange={event => setAgeGroupThreeShare(event.target.value ? +event.target.value : undefined)}
                      />
                    </div>
                  </form>
                </div>
                <div className="mb-4 w-full">
                  <form className="-mx-2.5 flex flex-wrap">
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap self-end sm:basis-[45%] lg:self-start">
                      <h4 className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:genders")}
                      </h4>
                      <span className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1">
                        {t("influencer_profile:women")}
                      </span>
                      <span className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1">
                        {t("influencer_profile:men")}
                      </span>
                    </div>
                    <div className="mx-2.5 mb-6 flex w-full flex-col flex-wrap self-end sm:basis-[45%] lg:self-start">
                      <h4 className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                        {t("influencer_profile:share")}
                      </h4>
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !femaleRatio,
                          },
                        )}
                        maxLength={256}
                        placeholder="50%"
                        value={femaleRatio ?? ""}
                        onChange={event => setFemaleRatio(event.target.value ? +event.target.value : undefined)}
                      />
                      <input
                        type="text"
                        className={clsxm(
                          "shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1",
                          {
                            "border-red-300": !maleRatio,
                          },
                        )}
                        maxLength={256}
                        placeholder="50%"
                        value={maleRatio ?? ""}
                        onChange={event => setMaleRatio(event.target.value ? +event.target.value : undefined)}
                      />
                    </div>
                  </form>
                  {insightsSuccess && (
                    <div>
                      <div>{t("influencer_profile:insights_updated")}</div>
                    </div>
                  )}
                </div>
                <button
                  className="bg-bold-blue-500 hover:shadow-highlight mr-2.5 min-h-10 min-w-10 cursor-pointer rounded-2xl border-none px-5 py-3 text-center font-medium tracking-wider text-white uppercase hover:scale-105"
                  onClick={handleInsightsUpdate}>
                  {t("Save")}
                </button>
                <FormErrors errors={insightsErrors} />
                {!confirm ? (
                  <button
                    className="hover:shadow-highlight mt-1 mr-2.5 min-h-10 min-w-10 cursor-pointer rounded-2xl border-none bg-red-300 px-5 py-3 text-center font-medium tracking-wider text-white uppercase hover:scale-105"
                    onClick={() => setConfirm(true)}>
                    {t("Delete")}
                  </button>
                ) : (
                  <button
                    className="hover:shadow-highlight mt-1 mr-2.5 min-h-10 min-w-10 cursor-pointer rounded-2xl border-none bg-red-300 px-5 py-3 text-center font-medium tracking-wider text-white uppercase hover:scale-105"
                    onClick={handleDelete}>
                    {t("Confirm?")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
